import { createAsyncThunk } from '@reduxjs/toolkit';
import focusBearApi from 'services/axios-config';
import Endpoints from 'constants/endpoints';
import { HTTP_STATS_CODE } from 'constants/general';
import { PROJECT_NOTE } from 'constants/project-note';
import { toast } from 'react-toastify';
import { t } from 'services/i18n.service';
import { captureException } from 'services/posthog.service';
import {
  ProjectNote,
  ProjectNoteListResponse,
  FetchProjectNotesParams,
  ProjectNoteComment,
  ProjectNoteCommentListResponse,
  EmbeddableTask,
  FetchEmbeddableTasksArgs,
  FetchEmbeddableTasksByIdsArgs,
  ProjectNoteIds,
  ProjectNoteDocResponse,
  CreateProjectNoteArgs,
  UpdateProjectNoteArgs,
  AddNoteCommentArgs,
  DeleteNoteCommentArgs
} from 'interfaces/project-note.interface';

export const fetchProjectNotes = createAsyncThunk(
  'projectNote/fetch_notes',
  async ({ projectId, page = 1, take = 20 }: FetchProjectNotesParams) => {
    try {
      const { data } = await focusBearApi.get<ProjectNoteListResponse>(
        Endpoints.PROJECT_NOTES(projectId),
        { params: { page, take } }
      );
      return data;
    } catch (error) {
      captureException(JSON.stringify(error));
      return null;
    }
  }
);

export const fetchProjectNoteById = createAsyncThunk(
  'projectNote/fetch_note_by_id',
  async ({ projectId, noteId }: ProjectNoteIds) => {
    try {
      const { data } = await focusBearApi.get<ProjectNote>(
        Endpoints.PROJECT_NOTE_BY_ID(projectId, noteId)
      );
      return data;
    } catch (error) {
      captureException(JSON.stringify(error));
      return null;
    }
  }
);

/** Yjs doc snapshot — returned to caller; not stored in Redux slice. */
export const fetchProjectNoteDoc = createAsyncThunk(
  'projectNote/fetch_note_doc',
  async ({
    projectId,
    noteId
  }: ProjectNoteIds): Promise<ProjectNoteDocResponse | null> => {
    try {
      const { data } = await focusBearApi.get<ProjectNoteDocResponse>(
        Endpoints.PROJECT_NOTE_DOC(projectId, noteId)
      );
      return data;
    } catch (error) {
      captureException(JSON.stringify(error));
      return null;
    }
  }
);

export const createProjectNote = createAsyncThunk(
  'projectNote/create_note',
  async ({
    projectId,
    payload
  }: CreateProjectNoteArgs) => {
    try {
      const { data, status } = await focusBearApi.post<ProjectNote>(
        Endpoints.PROJECT_NOTES(projectId),
        payload
      );
      if (status === HTTP_STATS_CODE.CREATED) {
        return data;
      }
      toast.error(t('project_note.could_not_create'));
      return null;
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
      return null;
    }
  }
);

export const updateProjectNote = createAsyncThunk(
  'projectNote/update_note',
  async ({
    projectId,
    noteId,
    payload
  }: UpdateProjectNoteArgs) => {
    try {
      const { data, status } = await focusBearApi.patch<ProjectNote>(
        Endpoints.PROJECT_NOTE_BY_ID(projectId, noteId),
        payload
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        return data;
      }
      toast.error(t('project_note.could_not_update'));
      return null;
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
      return null;
    }
  }
);

export const deleteProjectNote = createAsyncThunk(
  'projectNote/delete_note',
  async ({ projectId, noteId }: ProjectNoteIds) => {
    try {
      const { status } = await focusBearApi.delete(
        Endpoints.PROJECT_NOTE_BY_ID(projectId, noteId)
      );
      if (
        status === HTTP_STATS_CODE.NO_CONTENT ||
        status === HTTP_STATS_CODE.SUCCESS
      ) {
        toast.success(t('project_note.deleted_successfully'));
        return noteId;
      }
      toast.error(t('project_note.could_not_delete'));
      return null;
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
      return null;
    }
  }
);

export const fetchNoteComments = createAsyncThunk(
  'projectNote/fetch_comments',
  async ({ projectId, noteId }: ProjectNoteIds) => {
    try {
      const { data } = await focusBearApi.get<ProjectNoteCommentListResponse>(
        Endpoints.PROJECT_NOTE_COMMENTS(projectId, noteId),
        {
          params: {
            page: PROJECT_NOTE.EMBED.PAGE,
            take: PROJECT_NOTE.EMBED.PAGE_SIZE
          }
        }
      );
      return data;
    } catch (error) {
      captureException(JSON.stringify(error));
      return null;
    }
  }
);

export const addNoteComment = createAsyncThunk(
  'projectNote/add_comment',
  async ({
    projectId,
    noteId,
    body
  }: AddNoteCommentArgs) => {
    try {
      const { data, status } = await focusBearApi.post<ProjectNoteComment>(
        Endpoints.PROJECT_NOTE_COMMENTS(projectId, noteId),
        { body }
      );
      if (status === HTTP_STATS_CODE.CREATED) {
        return data;
      }
      toast.error(t('project_note.could_not_comment'));
      return null;
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
      return null;
    }
  }
);

export const deleteNoteComment = createAsyncThunk(
  'projectNote/delete_comment',
  async ({
    projectId,
    noteId,
    commentId
  }: DeleteNoteCommentArgs) => {
    try {
      const { status } = await focusBearApi.delete(
        Endpoints.PROJECT_NOTE_COMMENT_BY_ID(projectId, noteId, commentId)
      );
      if (
        status === HTTP_STATS_CODE.NO_CONTENT ||
        status === HTTP_STATS_CODE.SUCCESS
      ) {
        return commentId;
      }
      toast.error(t('project_note.could_not_delete_comment'));
      return null;
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
      return null;
    }
  }
);

export const fetchEmbeddableTasks = createAsyncThunk(
  'projectNote/fetch_embeddable_tasks',
  async ({
    projectId,
    page = PROJECT_NOTE.EMBED.PAGE,
    take = PROJECT_NOTE.EMBED.PAGE_SIZE,
    status
  }: FetchEmbeddableTasksArgs): Promise<EmbeddableTask[]> => {
    try {
      const { data } = await focusBearApi.get<{ data: EmbeddableTask[] }>(
        Endpoints.PROJECT_TODOS(projectId),
        {
          params: {
            page,
            take,
            ...(status != null && status.length > 0 ? { status } : {})
          }
        }
      );
      return data?.data ?? [];
    } catch (error) {
      captureException(JSON.stringify(error));
      return [];
    }
  }
);

export const fetchEmbeddableTasksByIds = createAsyncThunk(
  'projectNote/fetch_embeddable_tasks_by_ids',
  async ({
    projectId,
    ids
  }: FetchEmbeddableTasksByIdsArgs): Promise<EmbeddableTask[]> => {
    try {
      if (ids.length === 0) {
        return [];
      }
      const { data } = await focusBearApi.get<EmbeddableTask[]>(
        Endpoints.PROJECT_NOTE_EMBED_TASKS(projectId),
        { params: { ids: ids.join(PROJECT_NOTE.EMBED.TASK_IDS_SEPARATOR) } }
      );
      return data ?? [];
    } catch (error) {
      captureException(JSON.stringify(error));
      return [];
    }
  }
);
